import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth } from '../lib/auth'
import type { Credential, Device, DeviceTemplate } from '../lib/types'
import { PruefungenForm } from '../components/PruefungenForm'

interface Props {
  device: Device
  templates: DeviceTemplate[]
}

/** Antwort von POST /devices/{id}/template-erkennen. */
interface DetectResult {
  previous: string
  match: string
  sys_object_id: string
  changed: boolean
  reason: string
  device: Device
}

/** Zwecke, für die ein Gerät einen Zugang braucht. */
const PURPOSES = [
  { key: 'poll', label: 'SNMP-Abfrage', kinds: ['snmp_v2c', 'snmp_v3'] },
  { key: 'ssh', label: 'SSH-Terminal', kinds: ['ssh'] },
  { key: 'gui', label: 'Web-GUI', kinds: ['http_basic', 'api_token'] },
  { key: 'backup', label: 'Config-Sicherung', kinds: ['ssh', 'api_token'] },
  { key: 'api', label: 'Hersteller-API (Proxmox, UniFi, Omada)', kinds: ['api_token', 'http_basic'] },
] as const

/**
 * Stammdaten und Zugangsdaten eines bestehenden Geräts. Beides fällt im
 * Betrieb an: die Web-Oberfläche eines Proxmox-Hosts hört auf 8006, und ein
 * SSH-Zugang wird oft erst nachgereicht, wenn das Gerät schon angelegt ist.
 */
export function GeraetVerwaltung({ device, templates }: Props) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const mayEdit = can('device.manage')
  const mayAssign = can('credential.manage')
  if (!mayEdit && !mayAssign) return null

  return (
    <section className="card" id="zugangsdaten">
      <div className="card-head">
        <h2 className="card-title">Verwaltung</h2>
        <span className="muted" style={{ fontSize: 12 }}>
          Stammdaten und Zugangsdaten
        </span>
      </div>
      <div className="card-body stack" style={{ gap: 20 }}>
        {mayEdit ? (
          <StammdatenForm
            device={device}
            templates={templates}
            onSaved={() => {
              void queryClient.invalidateQueries({ queryKey: ['device', device.id] })
              void queryClient.invalidateQueries({ queryKey: ['device-metrics', device.id] })
            }}
          />
        ) : null}
        {mayEdit && templates.find((t) => t.key === device.template_key)?.checks ? (
          <PruefungenForm
            device={device}
            onSaved={() => void queryClient.invalidateQueries({ queryKey: ['device', device.id] })}
          />
        ) : null}
        {mayAssign ? (
          <ZugangsdatenForm
            device={device}
            onSaved={() => void queryClient.invalidateQueries({ queryKey: ['device', device.id] })}
          />
        ) : null}
      </div>
    </section>
  )
}

function StammdatenForm({
  device,
  templates,
  onSaved,
}: {
  device: Device
  templates: DeviceTemplate[]
  onSaved: () => void
}) {
  const [templateKey, setTemplateKey] = useState(device.template_key)
  const [guiScheme, setGuiScheme] = useState(device.gui_scheme)
  const [guiPort, setGuiPort] = useState(String(device.gui_port))
  const [sshPort, setSshPort] = useState(String(device.ssh_port))
  const [snmpPort, setSnmpPort] = useState(String(device.snmp_port))
  const [tunnelPorts, setTunnelPorts] = useState((device.tunnel_ports ?? []).join(', '))
  const [agentId, setAgentId] = useState(device.agent_id ?? '')

  // Nur Agents desselben Standorts: ein Gerät an einem fremden Agent würde
  // dessen Freigabeliste in ein fremdes Netz erweitern.
  const agents = useQuery({
    queryKey: ['agents', device.site_id],
    queryFn: ({ signal }) => apiGet<{ id: string; name: string }[]>(`/agents?standort=${device.site_id}`, signal),
  })

  const save = useMutation({
    mutationFn: () =>
      apiSend<Device>('PATCH', `/devices/${device.id}`, {
        template_key: templateKey,
        gui_scheme: guiScheme,
        gui_port: Number(guiPort),
        ssh_port: Number(sshPort),
        snmp_port: Number(snmpPort),
        tunnel_ports: tunnelPortListe(tunnelPorts),
        agent_id: agentId === '' ? null : agentId,
      }),
    onSuccess: onSaved,
  })

  // Ein Gerät wird angelegt, bevor es je abgefragt wurde, und startet deshalb
  // auf dem generischen Template. Erst die sysObjectID sagt, was es wirklich
  // ist — und ein passendes Template kann auch später dazukommen. Dieser Knopf
  // stellt die Frage neu.
  const detect = useMutation({
    mutationFn: () =>
      apiSend<DetectResult>('POST', `/devices/${device.id}/template-erkennen`),
    onSuccess: (res) => {
      if (res.changed) setTemplateKey(res.device.template_key)
      onSaved()
    },
  })

  // Wechselt das Template, schlägt dessen Vorgabe für die Oberfläche vor.
  function chooseTemplate(key: string) {
    setTemplateKey(key)
    const chosen = templates.find((t) => t.key === key)
    if (chosen?.gui_port) setGuiPort(String(chosen.gui_port))
    if (chosen?.gui_scheme) setGuiScheme(chosen.gui_scheme)
    if (chosen?.ssh_port) setSshPort(String(chosen.ssh_port))
  }

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <strong style={{ fontSize: 14 }}>Stammdaten</strong>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 220 }}>
          <label htmlFor="geraet-template">Template</label>
          <select
            id="geraet-template"
            className="input"
            value={templateKey}
            onChange={(e) => chooseTemplate(e.target.value)}
          >
            {templates.map((t) => (
              <option key={t.key} value={t.key}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <span className="sr-only" id="geraet-template-erkennen-hinweis">
            Template anhand der gemeldeten sysObjectID neu bestimmen
          </span>
          <button
            type="button"
            className="btn"
            aria-describedby="geraet-template-erkennen-hinweis"
            disabled={detect.isPending}
            onClick={() => detect.mutate()}
          >
            {detect.isPending ? 'Wird geprüft …' : 'Template neu erkennen'}
          </button>
        </div>
        <div className="field" style={{ width: 120 }}>
          <label htmlFor="geraet-gui-schema">GUI-Schema</label>
          <select
            id="geraet-gui-schema"
            className="input"
            value={guiScheme}
            onChange={(e) => setGuiScheme(e.target.value)}
          >
            <option value="https">https</option>
            <option value="http">http</option>
          </select>
        </div>
        <div className="field" style={{ width: 110 }}>
          <label htmlFor="geraet-gui-port">GUI-Port</label>
          <input
            id="geraet-gui-port"
            className="input mono"
            inputMode="numeric"
            value={guiPort}
            onChange={(e) => setGuiPort(e.target.value)}
          />
        </div>
        <div className="field" style={{ width: 110 }}>
          <label htmlFor="geraet-ssh-port">SSH-Port</label>
          <input
            id="geraet-ssh-port"
            className="input mono"
            inputMode="numeric"
            value={sshPort}
            onChange={(e) => setSshPort(e.target.value)}
          />
        </div>
        <div className="field" style={{ width: 110 }}>
          <label htmlFor="geraet-snmp-port">SNMP-Port</label>
          <input
            id="geraet-snmp-port"
            className="input mono"
            inputMode="numeric"
            value={snmpPort}
            onChange={(e) => setSnmpPort(e.target.value)}
          />
        </div>
        <div className="field" style={{ width: 170 }}>
          <label htmlFor="geraet-tunnel-ports">Tunnel-Ports</label>
          <input
            id="geraet-tunnel-ports"
            className="input mono"
            placeholder="z. B. 8291, 3389"
            value={tunnelPorts}
            onChange={(e) => setTunnelPorts(e.target.value)}
          />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label htmlFor="geraet-agent">Agent</label>
          <select
            id="geraet-agent"
            className="input"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            <option value="">kein Agent</option>
            {(agents.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {detect.isError ? (
        <div role="alert" style={{ color: 'var(--crit)' }}>
          {(detect.error as Error).message}
        </div>
      ) : null}
      {detect.isSuccess ? (
        <div role="status" style={{ color: detect.data.changed ? 'var(--ok)' : undefined }}>
          {detect.data.reason}
        </div>
      ) : null}
      {save.isError ? (
        <div role="alert" style={{ color: 'var(--crit)' }}>
          {(save.error as Error).message}
        </div>
      ) : null}
      {save.isSuccess ? <div style={{ color: 'var(--ok)' }}>Gespeichert.</div> : null}
      <div>
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Speichert …' : 'Stammdaten speichern'}
        </button>
      </div>
    </form>
  )
}

function ZugangsdatenForm({ device, onSaved }: { device: Device; onSaved: () => void }) {
  const [purpose, setPurpose] = useState<string>('ssh')
  const [credentialId, setCredentialId] = useState('')

  const credentials = useQuery({
    queryKey: ['credentials', device.customer_id],
    queryFn: ({ signal }) =>
      apiGet<Credential[]>(`/customers/${device.customer_id}/credentials`, signal),
  })

  const assign = useMutation({
    mutationFn: () =>
      apiSend<void>('PUT', `/devices/${device.id}/credentials`, {
        credential_id: credentialId,
        purpose,
      }),
    onSuccess: onSaved,
  })

  const wanted = PURPOSES.find((p) => p.key === purpose)
  const fitting = (credentials.data ?? []).filter(
    (c) => !wanted || (wanted.kinds as readonly string[]).includes(c.kind),
  )
  const assigned = device.credential_purposes ?? []

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault()
        if (credentialId) assign.mutate()
      }}
    >
      <strong style={{ fontSize: 14 }}>Zugangsdaten</strong>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {PURPOSES.map((p) => (
          <span
            key={p.key}
            className="muted"
            style={{ fontSize: 13, color: assigned.includes(p.key) ? 'var(--ok)' : undefined }}
          >
            {p.label}: {assigned.includes(p.key) ? 'hinterlegt' : 'nicht hinterlegt'}
          </span>
        ))}
      </div>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 200 }}>
          <label htmlFor="zugang-zweck">Verwendung</label>
          <select
            id="zugang-zweck"
            className="input"
            value={purpose}
            onChange={(e) => {
              setPurpose(e.target.value)
              setCredentialId('')
            }}
          >
            {PURPOSES.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 260 }}>
          <label htmlFor="zugang-eintrag">Zugang aus dem Tresor</label>
          <select
            id="zugang-eintrag"
            className="input"
            value={credentialId}
            onChange={(e) => setCredentialId(e.target.value)}
          >
            <option value="">bitte wählen</option>
            {fitting.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.kind})
              </option>
            ))}
          </select>
        </div>
      </div>
      {fitting.length === 0 && !credentials.isPending ? (
        <span className="muted" style={{ fontSize: 13 }}>
          Für diese Verwendung liegt kein passender Zugang beim Kunden. Zugänge werden unter
          Standorte &amp; Agents beim Kunden angelegt.
        </span>
      ) : null}
      {assign.isError ? (
        <div role="alert" style={{ color: 'var(--crit)' }}>
          {(assign.error as Error).message}
        </div>
      ) : null}
      {assign.isSuccess ? <div style={{ color: 'var(--ok)' }}>Zugang zugeordnet.</div> : null}
      <div>
        <button type="submit" className="btn btn-primary" disabled={assign.isPending || !credentialId}>
          {assign.isPending ? 'Ordnet zu …' : 'Zugang zuordnen'}
        </button>
      </div>
    </form>
  )
}

/** Wird auch auf der Geräteseite gebraucht: gibt es diesen Zugang schon? */
export function hasCredential(device: Device, purpose: string): boolean {
  return (device.credential_purposes ?? []).includes(purpose)
}

/** „8291, 3389“ → [8291, 3389]; was keine Zahl ist, prüft die Zentrale und meldet es. */
export function tunnelPortListe(text: string): number[] {
  return text
    .split(/[\s,;]+/)
    .filter((t) => t !== '')
    .map(Number)
}
